import React, { useCallback, useMemo, useState } from 'react';
import { Button, Card, Descriptions, Drawer, Empty, message, Spin, Table, Tag } from 'antd';
import { EyeOutlined, FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import orderService from '../../../services/orderService';
import {
  resolveOrderStatusColor,
  resolveOrderStatusLabel,
} from '../../product-master/pre-ipo/order-management/orderStatus';
import {
  buildDealSheetPdfFilename,
  dealSheetPdfHref,
  formatDealSheetStampDuty,
  formatDealSheetTds,
  formatDealSheetAmount,
  formatInvestorCurrency,
  formatInvestorFee,
  formatInvestorTransDate,
  getInvestorPreIpoStatusColor,
  getInvestorPreIpoStatusLabel,
  triggerDealSheetPdfDownload,
} from '../commons/investorProfile.helpers';
import GenerateDealSheetModal from './GenerateDealSheetModal';

const formatDateTime = (value) =>
  value ? dayjs(value).format('DD MMM YYYY HH:mm') : '—';

export default function PreIpoTransactionsTab({
  transactions = [],
  investorName,
  onRefresh,
  onDealSheetUpdated,
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerOrder, setDrawerOrder] = useState(null);
  const [dealSheetModalOpen, setDealSheetModalOpen] = useState(false);
  const [dealSheetTarget, setDealSheetTarget] = useState(null);
  const [dealSheetSubmitting, setDealSheetSubmitting] = useState(false);
  const [dealSheetPreviewLoading, setDealSheetPreviewLoading] = useState(false);

  const IS_DEV = import.meta.env.DEV;

  const openOrderDrawer = useCallback(async (row) => {
    const orderUuid = row?.uuid;
    if (!orderUuid) return;
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDrawerOrder(null);
    try {
      const detail = await orderService.adminGetOrderDetail(orderUuid);
      setDrawerOrder(detail?.order ?? detail);
    } catch {
      setDrawerOrder(null);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  const openDealSheetModal = useCallback((row) => {
    setDealSheetTarget(row);
    setDealSheetModalOpen(true);
  }, []);

  const handleDownloadDealSheet = useCallback(
    (row) => {
      const url = dealSheetPdfHref(row.manual_deal_sheet_url, row.manual_deal_sheet_generated_at);
      if (!url) return;
      triggerDealSheetPdfDownload(url, buildDealSheetPdfFilename(row, investorName));
    },
    [investorName]
  );

  const handleGenerateDealSheet = useCallback(
    async (options) => {
      if (!dealSheetTarget?.uuid) return;
      setDealSheetSubmitting(true);
      const targetRow = dealSheetTarget;
      try {
        const result = await orderService.generateDealSheet(dealSheetTarget.uuid, {
          ...options,
          force_regenerate:
            options.force_regenerate || Boolean(dealSheetTarget.manual_deal_sheet_url),
        });
        const url = dealSheetPdfHref(result?.url, result?.generated_at);
        const dealSheetOptions = result?.manual_deal_sheet_options ?? {
          include_tds: options.include_tds === true,
          include_stamp_duty: options.include_stamp_duty !== false,
        };
        onDealSheetUpdated?.(targetRow.uuid, {
          manual_deal_sheet_url: result?.manual_deal_sheet_url ?? targetRow.manual_deal_sheet_url,
          manual_deal_sheet_generated_at: result?.generated_at ?? targetRow.manual_deal_sheet_generated_at,
          manual_deal_sheet_options: dealSheetOptions,
          ...(result?.stamp_duty != null ? { stamp_duty: result.stamp_duty } : {}),
          ...(result?.tcs != null ? { tcs: result.tcs } : {}),
          ...(result?.total_payable != null
            ? { total_payable: result.total_payable, amount: result.total_payable }
            : {}),
        });
        message.success(
          result?.cached ? 'Deal sheet already available.' : 'Deal sheet generated successfully.'
        );
        setDealSheetModalOpen(false);
        setDealSheetTarget(null);
        if (onRefresh) await onRefresh();
        if (url) {
          triggerDealSheetPdfDownload(
            url,
            buildDealSheetPdfFilename(targetRow, investorName, result?.generated_at)
          );
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message || err?.message || 'Failed to generate deal sheet.'
        );
      } finally {
        setDealSheetSubmitting(false);
      }
    },
    [dealSheetTarget, investorName, onRefresh, onDealSheetUpdated]
  );

  const handlePreviewDealSheet = useCallback(
    async (options) => {
      if (!dealSheetTarget?.uuid) return;
      setDealSheetPreviewLoading(true);
      try {
        const blob = await orderService.previewDealSheet(dealSheetTarget.uuid, {
          include_tds: options.include_tds,
          include_stamp_duty: options.include_stamp_duty,
        });
        const url = URL.createObjectURL(
          new Blob([blob], { type: 'application/pdf' })
        );
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (err) {
        let errMsg = err?.message || 'Failed to preview deal sheet.';
        const blobBody = err?.response?.data;
        if (blobBody instanceof Blob) {
          try {
            const parsed = JSON.parse(await blobBody.text());
            errMsg = parsed?.message || errMsg;
          } catch {
            /* ignore */
          }
        } else if (err?.response?.data?.message) {
          errMsg = err.response.data.message;
        }
        message.error(errMsg);
      } finally {
        setDealSheetPreviewLoading(false);
      }
    },
    [dealSheetTarget]
  );

  const renderManualDealSheet = useCallback(
    (_, row) => {
      const url = row.manual_deal_sheet_url;
      const canRegenerate =
        Boolean(url) &&
        row.can_regenerate_deal_sheet !== false &&
        row.can_edit_deal_sheet_fees !== false;
      const canGenerateFirst = !url && row.can_generate_deal_sheet !== false;

      if (url) {
        return (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleDownloadDealSheet(row)}
              className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm bg-transparent border-0 p-0 cursor-pointer"
            >
              <FilePdfOutlined />
              PDF
            </button>
            {canRegenerate ? (
              <Button
                type="primary"
                size="small"
                className="font-semibold"
                onClick={() => openDealSheetModal(row)}
              >
                Regenerate
              </Button>
            ) : null}
            {IS_DEV ? (
              <Button
                size="small"
                onClick={() => openDealSheetModal(row)}
              >
                Preview (dev)
              </Button>
            ) : null}
          </div>
        );
      }

      if (!canGenerateFirst) {
        return IS_DEV ? (
          <Button size="small" onClick={() => openDealSheetModal(row)}>
            Preview (dev)
          </Button>
        ) : (
          '—'
        );
      }

      return (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="primary"
            size="small"
            className="font-semibold"
            onClick={() => openDealSheetModal(row)}
          >
            Generate
          </Button>
          {IS_DEV ? (
            <Button size="small" onClick={() => openDealSheetModal(row)}>
              Preview (dev)
            </Button>
          ) : null}
        </div>
      );
    },
    [openDealSheetModal, handleDownloadDealSheet, IS_DEV]
  );

  const columns = useMemo(
    () => [
      {
        title: 'Trans. Date',
        dataIndex: 'transaction_date',
        key: 'transaction_date',
        sorter: (a, b) =>
          new Date(a.transaction_date).getTime() - new Date(b.transaction_date).getTime(),
        render: formatInvestorTransDate,
      },
      {
        title: 'Request ID',
        dataIndex: 'order_id',
        key: 'order_id',
        sorter: (a, b) => String(a.order_id || '').localeCompare(String(b.order_id || '')),
        render: (value) => <span className="font-mono text-sm">{value}</span>,
      },
      {
        title: 'Share Name',
        dataIndex: 'share_name',
        key: 'share_name',
      },
      {
        title: 'Rqst Qty',
        dataIndex: 'requested_qty',
        key: 'requested_qty',
        align: 'right',
      },
      {
        title: 'Price',
        dataIndex: 'price',
        key: 'price',
        align: 'right',
        render: (value) => formatInvestorCurrency(value),
      },
      {
        title: 'TDS',
        dataIndex: 'tcs',
        key: 'tcs',
        align: 'right',
        render: (_, row) => formatDealSheetTds(row),
      },
      {
        title: 'Stamp Duty',
        dataIndex: 'stamp_duty',
        key: 'stamp_duty',
        align: 'right',
        render: (_, row) => formatDealSheetStampDuty(row),
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        align: 'right',
        render: (_, row) => (
          <span className="font-semibold">{formatDealSheetAmount(row)}</span>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (_, row) => (
          <Tag color={getInvestorPreIpoStatusColor(row)}>
            {getInvestorPreIpoStatusLabel(row)}
          </Tag>
        ),
      },
      {
        title: 'Deal Slip',
        dataIndex: 'deal_slip',
        key: 'deal_slip',
        render: (url) =>
          url ? (
            <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
              PDF
            </a>
          ) : (
            '—'
          ),
      },
      {
        title: 'Manual Deal Sheet',
        key: 'manual_deal_sheet',
        width: 130,
        render: renderManualDealSheet,
      },
      {
        title: 'View',
        key: 'view',
        fixed: 'right',
        width: 72,
        render: (_, row) => (
          <button
            type="button"
            className="text-gray-600 hover:text-emerald-700 p-1"
            onClick={() => openOrderDrawer(row)}
            aria-label="View order"
          >
            <EyeOutlined />
          </button>
        ),
      },
    ],
    [openOrderDrawer, renderManualDealSheet]
  );

  const statusLogCols = [
    { title: 'From', dataIndex: 'from_status', key: 'from', width: 140 },
    { title: 'To', dataIndex: 'to_status', key: 'to', width: 140 },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', ellipsis: true },
    { title: 'At', dataIndex: 'created_at', key: 'at', width: 160, render: formatDateTime },
    { title: 'By', dataIndex: 'changed_by_name', key: 'by', width: 120 },
  ];

  return (
    <>
      <Card bordered={false} className="rounded-2xl shadow-sm border border-gray-100">
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey={(record) => record.uuid || record.id}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `Total ${total} items`,
          }}
          locale={{ emptyText: <Empty description="No Pre-IPO transactions found." /> }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      <GenerateDealSheetModal
        open={dealSheetModalOpen}
        orderRow={dealSheetTarget}
        loading={dealSheetSubmitting}
        previewLoading={dealSheetPreviewLoading}
        onCancel={() => {
          setDealSheetModalOpen(false);
          setDealSheetTarget(null);
        }}
        onGenerate={handleGenerateDealSheet}
        onPreview={IS_DEV ? handlePreviewDealSheet : undefined}
      />

      <Drawer
        title={drawerOrder ? `Order ${drawerOrder.order_number}` : 'Order details'}
        width={720}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerOrder(null);
        }}
        destroyOnClose
      >
        {drawerLoading ? (
          <div className="flex justify-center py-12">
            <Spin size="large" />
          </div>
        ) : drawerOrder ? (
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Status">
              <Tag color={resolveOrderStatusColor(drawerOrder)}>
                {resolveOrderStatusLabel(drawerOrder)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Order #">{drawerOrder.order_number}</Descriptions.Item>
            <Descriptions.Item label="Product">{drawerOrder.product_name}</Descriptions.Item>
            <Descriptions.Item label="Quantity">{drawerOrder.quantity}</Descriptions.Item>
            <Descriptions.Item label="Unit price">
              {formatInvestorCurrency(drawerOrder.unit_price)}
            </Descriptions.Item>
            <Descriptions.Item label="Total payable">
              {formatInvestorCurrency(drawerOrder.total_payable)}
            </Descriptions.Item>
            <Descriptions.Item label="Stamp duty">
              {formatInvestorFee(drawerOrder.stamp_duty)}
            </Descriptions.Item>
            <Descriptions.Item label="TDS">{formatInvestorFee(drawerOrder.tcs)}</Descriptions.Item>
            <Descriptions.Item label="UTR">{drawerOrder.utr_number || '—'}</Descriptions.Item>
            <Descriptions.Item label="Investor">{drawerOrder.investor_name}</Descriptions.Item>
            <Descriptions.Item label="Email">{drawerOrder.investor_email}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty description="Could not load order details." />
        )}
        {drawerOrder?.status_log?.length > 0 ? (
          <div className="mt-6">
            <div className="font-medium text-gray-900 mb-2">Status history</div>
            <Table
              size="small"
              rowKey={(r, i) => `${r.created_at}-${i}`}
              columns={statusLogCols}
              dataSource={drawerOrder.status_log}
              pagination={false}
            />
          </div>
        ) : null}
      </Drawer>
    </>
  );
}
